import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";
import { isCompanyFinanceEnabled } from "../../../config/companyFinance";

import { fetchCompany, fetchMyCompany } from "../api/companyWorkspace";
import {
  canOrdinaryAdminWrite,
  companyStatusLabel,
  isActiveMember,
  membershipStatusLabel,
  roleLabel,
} from "../lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../lib/scopeSession";
import { useCompanyFocusRevalidation } from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

function CompanyProfileContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  stateRef.current = state;
  const [error, setError] = useState(null);

  const load = useCallback(({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ companyId, actorId });
    if (!keepVisible) {
      setState("loading");
      setError(null);
    }
    return Promise.all([fetchCompany(companyId), fetchMyCompany()])
      .then(([companyData, mine]) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setCompany(companyData);
        const mineMatches =
          mine?.company?.id != null &&
          sameCompanyId(mine.company.id, companyId)
            ? mine.membership
            : null;
        setMembership(mineMatches);
        setAuthorityActorId(actorId);
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const status = err?.response?.status;
        if (keepVisible && status !== 403 && status !== 404) {
          return;
        }
        setCompany(null);
        setMembership(null);
        setAuthorityActorId(null);
        if (status === 404) setState("not-found");
        else if (status === 403) setState("forbidden");
        else {
          setError(messageFromError(err));
          setState("error");
        }
      });
  }, [actorId, companyId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: false,
    onRevalidate: () => load({ background: true }),
  });

  if (state === "loading") {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження профілю" />
      </div>
    );
  }

  if (state === "not-found") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="Компанію не знайдено"
          description="Перевірте адресу або поверніться до пошуку."
          size="md"
        />
        <Link to="/companies/find" className="action-btn">
          Знайти компанію
        </Link>
      </div>
    );
  }

  if (state === "forbidden") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу"
          description="Ця компанія недоступна для перегляду."
          size="md"
        />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Помилка завантаження"
          description={error || "Спробуйте оновити сторінку."}
          size="md"
        />
        <button type="button" className="action-btn" onClick={load}>
          Повторити
        </button>
      </div>
    );
  }

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatchesRoute && actorMatches;
  if (!authorityMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження профілю" />
      </div>
    );
  }

  const memberView = isActiveMember(company, membership);
  const admin = canOrdinaryAdminWrite(company, membership);
  const ownStatus = membership?.status || company?.my_membership?.status;
  const openToJoin = Boolean(company.is_open_to_join) && company.status === "active";

  return (
    <section className="clients-page feature-company-workspace" id="Csection" tabIndex="-1">
      <div className="container">
        <CompanyPageHeader
          title={company.name}
          subtitle={
            memberView
              ? "Профіль вашої компанії"
              : "Публічний профіль компанії"
          }
          company={memberView ? company : null}
          membership={membership}
          companyId={memberView ? company.id : null}
          activeNav="profile"
          actions={
            <>
              {openToJoin && !memberView ? (
                <Link
                  to={`/companies/join-request?companyId=${company.id}`}
                  className="action-btn"
                >
                  Подати заявку
                </Link>
              ) : null}
              {admin ? (
                <Link
                  to={`/companies/${company.id}/settings`}
                  className="action-btn"
                >
                  Налаштування
                </Link>
              ) : null}
            </>
          }
        />

        {ownStatus === "suspended" ? (
          <div className="feature-company-workspace__banner" role="status">
            Ваше членство призупинено. Показано лише безпечний публічний профіль.
            {isCompanyFinanceEnabled() ? (
              <>
                {" "}
                <Link
                  to={`/companies/${company.id}/my-remuneration`}
                  data-testid="company-profile-remuneration-cta"
                >
                  Моя винагорода
                </Link>
              </>
            ) : null}
          </div>
        ) : null}

        {ownStatus === "ended" && isCompanyFinanceEnabled() ? (
          <div className="feature-company-workspace__banner" role="status">
            Членство завершено. Корпоративна навігація недоступна.{" "}
            <Link
              to={`/companies/${company.id}/my-remuneration`}
              data-testid="company-profile-remuneration-cta-ended"
            >
              Моя винагорода
            </Link>
          </div>
        ) : null}

        {company.status === "archived" ? (
          <div className="feature-company-workspace__banner" role="status">
            Компанія в архіві ({companyStatusLabel(company.status)}). Звичайні
            зміни недоступні.
          </div>
        ) : null}

        {(company.logo || company.banner) && (
          <div className="feature-company-workspace__branding-preview">
            {company.banner ? (
              <img
                className="feature-company-workspace__brand-banner"
                src={company.banner}
                alt=""
              />
            ) : null}
            {company.logo ? (
              <img
                className="feature-company-workspace__brand-logo"
                src={company.logo}
                alt={`Логотип ${company.name}`}
              />
            ) : null}
          </div>
        )}

        <article className="feature-company-workspace__card">
          <dl className="feature-company-workspace__facts">
            <div>
              <dt>Статус</dt>
              <dd>{companyStatusLabel(company.status)}</dd>
            </div>
            <div>
              <dt>Місто</dt>
              <dd>{company.city || "—"}</dd>
            </div>
            <div>
              <dt>Відкрита до вступу</dt>
              <dd>{company.is_open_to_join ? "Так" : "Ні"}</dd>
            </div>
            {memberView ? (
              <div>
                <dt>Моя роль</dt>
                <dd>{roleLabel(membership?.role || company.my_role)}</dd>
              </div>
            ) : null}
            {ownStatus ? (
              <div>
                <dt>Моє членство</dt>
                <dd>{membershipStatusLabel(ownStatus)}</dd>
              </div>
            ) : null}
            {memberView && company.email ? (
              <div>
                <dt>Email</dt>
                <dd>{company.email}</dd>
              </div>
            ) : null}
            {memberView && company.phone ? (
              <div>
                <dt>Телефон</dt>
                <dd>{company.phone}</dd>
              </div>
            ) : null}
            {company.website ? (
              <div>
                <dt>Вебсайт</dt>
                <dd>
                  <a href={company.website} rel="noopener noreferrer" target="_blank">
                    {company.website}
                  </a>
                </dd>
              </div>
            ) : null}
            <div>
              <dt>Учасників</dt>
              <dd>
                {company.members_count != null ? company.members_count : "—"}
              </dd>
            </div>
          </dl>
          {company.description ? (
            <p className="feature-company-workspace__description">
              {company.description}
            </p>
          ) : null}
        </article>

        {!memberView ? (
          <div className="feature-company-workspace__inline-actions">
            <Link to="/companies/find" className="action-btn action-btn--ghost">
              Назад до пошуку
            </Link>
            <Link to="/companies/mine" className="action-btn action-btn--ghost">
              Моя компанія
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default CompanyProfileContent;
